//@flow
import React from 'react';
import {Link} from 'react-router-dom';
import {makeStyles, Typography} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';
import {Store} from '@material-ui/icons';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {timeAgo} from '../../../../utils/date/timeAgo';
import {useTrainBotActions} from '../../hooks';
import {useToastActions} from '../../../toast-alert/utils';
import type {StoreChatbot} from '../../../entities';

const useStyles = makeStyles(theme => ({
  updateStoreInformationContainer: {
    background: 'linear-gradient(to bottom, #f4f0ff, #ffffff)',
    borderRadius: '8px',
    padding: '1rem',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '.75rem',

    [theme.breakpoints.up('md')]: {
      padding: '1rem',
    },
  },
  icon: {
    color: colors.primary,
  },
  label: {
    fontWeight: 600,
    color: colors.grey800,
  },
  caption: {
    fontSize: '.875rem',
    color: colors.grey800,
  },
  lastUpdated: {
    marginTop: '0.5rem',
    fontSize: '0.875rem',
    color: colors.grey600,
    fontStyle: 'italic',
    fontWeight: 500,
  },
  submitButton: {
    maxWidth: '200px',
    marginTop: '1rem',
  },
  cardsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoCard: {
    width: '120px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${colors.grey300}`,
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: colors.grey50,
    transition: 'all 0.3s ease',
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: colors.grey100,
      borderColor: colors.primary,
      transform: 'scale(1.05)',
    },
  },
  cardTitle: {
    fontSize: '0.75rem',
    textAlign: 'center',
    color: colors.grey800,
  },
}));

type Props = {
  chatBot: StoreChatbot,
  onSaveSuccess: () => void,
  lastStoredTraining?: {
    updatedAt: string,
  },
};

const infoCards = [
  {key: 'BLOG', label: 'Blog', path: '/my-store/blog'},
  {key: 'REFUNDS', label: 'Reembolsos', path: '/my-store/pages/legal/refunds'},
  {
    key: 'FAQ',
    label: 'Preguntas Frecuentes',
    path: '/my-store/pages/legal/faq',
  },
  {key: 'ABOUT_US', label: 'Sobre Nosotros', path: '/configure/customize'},
  {key: 'CATEGORIES', label: 'Categorías', path: '/categories'},
];

const TrainingTabStoreInfo = React.memo(function TrainingTabStoreInfo(
  props: Props
) {
  const classes = useStyles();
  const {showToast} = useToastActions();
  const {trainBot, loading} = useTrainBotActions();

  const handleTrain = async () => {
    if (!props.chatBot?.id) return;

    try {
      await trainBot(props.chatBot.id, {
        trainingType: 'STORE_INFORMATION',
      });

      showToast({
        message:
          'Entrenamiento con información de la tienda iniciado correctamente',
        title: 'Entrenamiento iniciado',
        type: 'success',
        duration: 3000,
      });

      props.onSaveSuccess();
    } catch (error) {
      const errorMessage =
        error?.message || 'Error desconocido durante el entrenamiento';
      showToast({
        message: errorMessage,
        title: 'Error en el entrenamiento',
        type: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <div className={classes.updateStoreInformationContainer}>
      <div className={classes.content}>
        <span className={classes.icon}>
          <Store fontSize="large" />
        </span>
        <span className={classes.label}>
          ¿Actualizaste la información de tu tienda?
        </span>
        <Typography variant="caption" className={classes.caption}>
          Entrena tu bot con la nueva información de tu tienda.
        </Typography>
        {props.lastStoredTraining && (
          <Typography
            variant="caption"
            className={classes.caption}
            style={{
              marginTop: '0.5rem',
              fontStyle: 'italic',
              fontWeight: 500,
              color: colors.grey600,
            }}
          >
            Última actualización:
            {timeAgo(new Date(props.lastStoredTraining.updatedAt).getTime())}
          </Typography>
        )}
        <CustomButton
          variant="contained"
          color="primary"
          className={classes.submitButton}
          onClick={handleTrain}
          disabled={loading}
          fullWidth
        >
          {loading ? 'Actualizando...' : 'Actualizar información'}
        </CustomButton>
      </div>
      <div>
        <Typography
          variant="subtitle2"
          align="center"
          style={{fontWeight: 600, color: colors.grey800, marginBlock: '.5rem'}}
        >
          Secciones que puedes actualizar:
        </Typography>
        <div className={classes.cardsContainer}>
          {infoCards.map(card => (
            <Link
              key={card.key}
              className={classes.infoCard}
              to={card.path}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className={classes.cardTitle}>{card.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
});

export default TrainingTabStoreInfo;
